import React from 'react';
import PropTypes from 'prop-types';
import { Alert, AlertTitle, Button, Card, CircularProgress, Link, TextField } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { AppBadge, AppDataTable } from '@shared';

const propTypes = {
  editTracking: PropTypes.bool,
  handleOrderChange: PropTypes.func,
  uber: PropTypes.bool, // If true and not show display uber_valid (for form)
  isShow: PropTypes.bool, // If true and uber display uber_order_id

  onCancelDelivery: PropTypes.func,
  loading: PropTypes.bool, // Only used when onCancelDelivery is present
  // URL to download the Fruno PDF. This points to the parent DispatchControl. To download specific orders, use the query param `dispatch_order_ids[]`
  pdfURLFruno: PropTypes.string,
  timelineBaseURL: PropTypes.string,
  providerName: PropTypes.string,
}

const defaultProps = {
  editTracking: false,
  uber: false,
  isShow: false,
  timelineBaseURL: null,
  providerName: null,
}

const DisplayControlOrders = (props) => {
  const { orders, editTracking, uber } = props;

  const displayUberValid = props.uber && !props.isShow;
  const displayUberOrderId = props.uber && props.isShow;

  let records = [];
  let rowNumber = 1;
  if (props.orders.length > 0) {
    for (let order of props.orders) {
      let parsedOrder = order;
      const received_amount = order.display_balance || null;

      // TrackingNumber cell depends on certain conditions
      // Fruno Orders display the `fruno_order_id` and a button to download the PDF
      // Non-editable orders display the `tracking_number`
      // Editable orders display `tracking_number` and a text field to edit it
      let trackingNumberCell;
      if(!!parsedOrder.fruno_order_id && !!props.pdfURLFruno){
        const pdfUrlForIndividualOrder = `${props.pdfURLFruno}?dispatch_order_ids[]=${parsedOrder.id}`;
        trackingNumberCell = (
          <Button
            variant="text"
            color="inherit"
            href={pdfUrlForIndividualOrder}
            target="_blank"
            rel="noopener"
            endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
          >
            { parsedOrder.fruno_order_id }
          </Button>
        )
      }
      else if(!props.editTracking || order.read_only){
        trackingNumberCell = parsedOrder.tracking_number || parsedOrder.mail_americas_tracking;
      }
      else{
        trackingNumberCell = (
          <TextField
            size="small"
            id={ order.provider_id }
            value={ order.tracking_number }
            onChange={ (event) => props.handleOrderChange(event.target.value, order.provider_id) }
          />
        )
      }

      let id = parsedOrder.display_shopify_id || parsedOrder.shopify_id;

      //add an order link if associated record exists
      if (parsedOrder.order_url) id = <Link href={parsedOrder.order_url} target="_blank" rel="noopener">{id}</Link>;

      if (props.timelineBaseURL && parsedOrder.brightpearl_order_id) {
        const timelineHref = `${props.timelineBaseURL}?order_id=${parsedOrder.brightpearl_order_id}&provider=${props.providerName}`;
        id = (
          <div>
            <div>{id}</div>
            <Link href={timelineHref} target="_blank" rel="noopener">Timeline</Link>
          </div>
        );
      }

      if (parsedOrder.delivery_address_changed) {
        id = (
          <div>
            {id}
            <div className="mt4">
              <AppBadge status="warning">Dirección editada</AppBadge>
            </div>
          </div>
        );
      }

      // ==============> BUILDING OF THE ROWS IS DONE HERE  <===============

      let rowData = [
        rowNumber,
        id,
        parsedOrder.name,
        parsedOrder.order_number,
        I18n.t(`order.financial_status.${parsedOrder.financial_status}`),
        received_amount,
        parsedOrder.brightpearl_custom_field_oc && I18n.t(String(parsedOrder.brightpearl_custom_field_oc)), //TRUE/FALSE (only display when true)
        parsedOrder.customer_procedure_kind,
        parsedOrder.pcf_zona,
        trackingNumberCell,
      ]

      if(displayUberValid){ // Display uber_valid (for form purposes)
        rowData.push(
          <div>
            <AppBadge status={parsedOrder.uber_valid ? "info" : "warning"}>
              {!!parsedOrder.uber_valid ? I18n.t("true") : I18n.t("false")}
            </AppBadge>
          </div>
        )
      }

      if(displayUberOrderId){ // Display uber_order_id (for show purposes after creation)
        rowData.push(
          parsedOrder.display_uber_uuid
        )
      }

      if (!!props.onCancelDelivery) {
        if (parsedOrder.cancellable) {
          rowData.push(
            <Button
              variant="text"
              color="error"
              onClick={() => props.onCancelDelivery(parsedOrder.id)}
              disabled={props.loading}
            >
              Cancelar Entrega
            </Button>
          );
        } else {
          rowData.push('');
        }
      }
      records.push(rowData),
      rowNumber +=1;
    }
  } 
  
  let headings = [
    "",
    I18n.t("activerecord.attributes.dispatch_order.provider_id"),
    I18n.t("activerecord.attributes.dispatch_order.name"),
    I18n.t("activerecord.attributes.dispatch_order.order_number"),
    I18n.t("activerecord.attributes.dispatch_order.financial_status"),
    I18n.t("activerecord.attributes.dispatch_order.received_amount"),
    I18n.t("activerecord.attributes.dispatch_order.brightpearl_custom_field_oc"),
    I18n.t("activerecord.attributes.dispatch_order.customer_procedure_kind"),
    I18n.t("activerecord.attributes.dispatch_order.pcf_zona"),
    I18n.t("activerecord.attributes.dispatch_order.tracking_number"),

  ]
  if(displayUberValid){
    headings = [
      ...headings, 
      I18n.t("activerecord.attributes.dispatch_order.uber_valid")
    ]
  }
  if(displayUberOrderId){
    headings = [
      ...headings,
      I18n.t("activerecord.attributes.dispatch_order.uber_order_id")
    ]
  }

  if (!!props.onCancelDelivery) {
    headings = [
      ...headings,
      props.loading ? <CircularProgress size={16} /> : ""
    ]
  }

  const hasAddressChangedOrders = props.orders.some(o => o.delivery_address_changed);

  return (
    <>
      { hasAddressChangedOrders &&
        <div className="mb16">
          <Alert severity="warning">
            <AlertTitle>Dirección de entrega editada</AlertTitle>
            Uno o más pedidos tienen una dirección diferente a la registrada originalmente. Verifique antes de despachar.
          </Alert>
        </div>
      }
      <Card>
      <AppDataTable
        headings={headings}
        rows={records}
        columnContentTypes={[
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
          'text',
        ]} />
      </Card>
    </>
  )
}

DisplayControlOrders.propTypes = propTypes;
DisplayControlOrders.defaultProps = defaultProps;

export default DisplayControlOrders;